import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyEdit = { direction: 'BUY', volume: '', entry_price: '', stop_loss: '', take_profit: '', status: 'PENDING', reason: '' };

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientQuery, setClientQuery] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('');
  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEdit);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orders`);
      setOrders(r.data);
    } catch (e) { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => orders.filter(o => {
    if (clientQuery && !`${o.client_name || ''} ${o.client_email || ''}`.toLowerCase().includes(clientQuery.toLowerCase())) return false;
    if (symbolFilter && !(o.symbol || '').toUpperCase().includes(symbolFilter.toUpperCase())) return false;
    if (directionFilter !== 'ALL' && o.direction !== directionFilter) return false;
    if (statusFilter !== 'ALL' && o.status !== statusFilter) return false;
    if (orderIdFilter && !(o.id || '').toLowerCase().includes(orderIdFilter.toLowerCase())) return false;
    if (dateFilter) {
      const d = new Date(o.created_at).toISOString().slice(0, 10);
      if (d !== dateFilter) return false;
    }
    return true;
  }), [orders, clientQuery, symbolFilter, directionFilter, statusFilter, dateFilter, orderIdFilter]);

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      direction: o.direction || 'BUY',
      volume: o.volume ?? '',
      entry_price: o.entry_price ?? '',
      stop_loss: o.stop_loss ?? '',
      take_profit: o.take_profit ?? '',
      status: o.status || 'PENDING',
      reason: '',
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        direction: form.direction,
        volume: form.volume === '' ? null : Number(form.volume),
        entry_price: form.entry_price === '' ? null : Number(form.entry_price),
        stop_loss: form.stop_loss === '' ? null : Number(form.stop_loss),
        take_profit: form.take_profit === '' ? null : Number(form.take_profit),
        status: form.status,
        reason: form.reason || 'Admin order adjustment',
      };
      await axios.put(`${API}/admin/orders/${editing.id}`, payload);
      toast.success('Order updated (audit log recorded)');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8" data-testid="admin-orders-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Orders</h1>
          <p className="text-slate-600 mt-1">{filtered.length} of {orders.length} orders</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="admin-orders-refresh">Refresh</Button>
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div><Label className="text-xs">Client (name/email)</Label><Input value={clientQuery} onChange={e=>setClientQuery(e.target.value)} placeholder="Search client…" data-testid="filter-client" /></div>
        <div><Label className="text-xs">Order ID</Label><Input value={orderIdFilter} onChange={e=>setOrderIdFilter(e.target.value)} placeholder="Order ID…" data-testid="filter-order-id" /></div>
        <div><Label className="text-xs">Symbol</Label><Input value={symbolFilter} onChange={e=>setSymbolFilter(e.target.value)} placeholder="EURUSD…" data-testid="filter-symbol" /></div>
        <div><Label className="text-xs">Direction</Label>
          <Select value={directionFilter} onValueChange={setDirectionFilter}>
            <SelectTrigger data-testid="filter-direction"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="BUY">BUY</SelectItem>
              <SelectItem value="SELL">SELL</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="EXECUTED">Executed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Date</Label><Input type="date" value={dateFilter} onChange={e=>setDateFilter(e.target.value)} data-testid="filter-date" /></div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-3">Client</th>
              <th className="p-3">Order ID</th>
              <th className="p-3">Symbol</th>
              <th className="p-3">Type</th>
              <th className="p-3">Direction</th>
              <th className="p-3 text-right">Volume</th>
              <th className="p-3 text-right">Entry Price</th>
              <th className="p-3 text-right">SL</th>
              <th className="p-3 text-right">TP</th>
              <th className="p-3">Status</th>
              <th className="p-3">Created</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="12" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
              : filtered.length === 0 ? <tr><td colSpan="12" className="p-8 text-center text-slate-500">No orders</td></tr>
              : filtered.map(order => (
                <tr key={order.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`order-row-${order.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{order.client_name}</div>
                    <div className="text-xs text-slate-500">{order.client_email}</div>
                  </td>
                  <td className="p-3 font-mono text-xs">{order.id.slice(0, 8)}…</td>
                  <td className="p-3 font-semibold">{order.symbol}</td>
                  <td className="p-3">{order.order_type}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${order.direction === 'BUY' ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-trading-bearish-bg text-trading-bearish'}`}>{order.direction}</span>
                  </td>
                  <td className="p-3 text-right tabular-nums">{order.volume}</td>
                  <td className="p-3 text-right tabular-nums">{order.entry_price?.toFixed(5) || '-'}</td>
                  <td className="p-3 text-right tabular-nums">{order.stop_loss?.toFixed(5) || '-'}</td>
                  <td className="p-3 text-right tabular-nums">{order.take_profit?.toFixed(5) || '-'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${order.status === 'EXECUTED' ? 'bg-green-100 text-green-800' : order.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{order.status}</span>
                  </td>
                  <td className="p-3 text-xs text-slate-500">{new Date(order.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <Button size="sm" variant="outline" onClick={() => openEdit(order)} data-testid={`edit-order-${order.id}`}>Edit</Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="edit-order-dialog">
          <DialogHeader>
            <DialogTitle>Edit Order · {editing?.symbol}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Direction</Label>
              <Select value={form.direction} onValueChange={v=>setForm({...form,direction:v})}>
                <SelectTrigger data-testid="edit-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BUY">BUY</SelectItem>
                  <SelectItem value="SELL">SELL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v=>setForm({...form,status:v})}>
                <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="EXECUTED">Executed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Volume</Label><Input type="number" step="0.01" value={form.volume} onChange={e=>setForm({...form,volume:e.target.value})} data-testid="edit-volume" /></div>
            <div><Label>Open / Entry Price</Label><Input type="number" step="0.00001" value={form.entry_price} onChange={e=>setForm({...form,entry_price:e.target.value})} data-testid="edit-entry-price" /></div>
            <div><Label>Stop Loss</Label><Input type="number" step="0.00001" value={form.stop_loss} onChange={e=>setForm({...form,stop_loss:e.target.value})} data-testid="edit-stop-loss" /></div>
            <div><Label>Take Profit</Label><Input type="number" step="0.00001" value={form.take_profit} onChange={e=>setForm({...form,take_profit:e.target.value})} data-testid="edit-take-profit" /></div>
            <div className="col-span-2"><Label>Reason / Note</Label><Input value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} placeholder="Reason for change (recorded in audit log)" data-testid="edit-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="edit-order-save">{saving ? 'Saving…' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
